import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Chip,
  Menu,
} from '@mui/material';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import { getRecordCardLocateActiveSx, listCardActionIconSx } from '../UserLotListsPanel/UserLotListsPanel.styles';
import { getDisplayUnits, getUnitMatchingListLabels, getLotMatchingListLabels } from '../../utils/userLotUnits';
import RecordLotIdentity, { RecordExpandButton, RecordLotContentIdentity, RecordUnitIdentity, recordCardHeaderIconSx } from '../RecordLotIdentity';
import { unitLocationDistinctFromLot } from '../../utils/recordLocation';
import { getLotContentSummary } from '../../utils/lotContentSummary';

const BG_ELEVATED = '#242424';
const BG_UNITS = '#141414';
const TEXT_PRIMARY = '#D4D4D4';
const BORDER = '#383838';
const CARD_PAD_X = 12;
const HEADER_GAP = 8;

const listMembershipChipSx = {
  height: 22,
  borderRadius: '4px',
  fontSize: '10.5px',
  fontWeight: 500,
  bgcolor: 'rgba(255,255,255,0.06)',
  color: TEXT_PRIMARY,
  border: `1px solid ${BORDER}`,
  maxWidth: '100%',
  '& .MuiChip-label': {
    px: '8px',
    py: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

/** Chips de las listas filtradas a las que pertenece el registro (también en la vista por registro). */
export const ListMembershipChips = ({ labels, singleLine = false }) => {
  if (!labels?.length) return null;
  return (
    // `singleLine`: card de registro de alto fijo; los chips que no entran se recortan.
    <Box sx={{ display: 'flex', flexWrap: singleLine ? 'nowrap' : 'wrap', gap: 0.5, ...(singleLine ? { overflow: 'hidden', minWidth: 0 } : null) }}>
      {labels.map((label) => (
        <Chip key={label.id} size="small" label={label.name} sx={listMembershipChipSx} />
      ))}
    </Box>
  );
};

/**
 * Card de lote para el panel REGISTROS (misma estructura visual que ListDetailLotCard).
 */
export default function RegistrosLotCard({
  lot,
  isExpanded,
  onToggleExpand,
  onOpenLot,
  onOpenUnit,
  onLocateLot,
  locateActive = false,
  onShareLot,
  showListMembership = false,
  listFilters = [],
  userLists = [],
  noListsValue = '__NO_LISTS__',
}) {
  const [lotMenuAnchor, setLotMenuAnchor] = useState(null);
  const [unitMenu, setUnitMenu] = useState({ anchor: null, unit: null });

  const units = getDisplayUnits(lot);
  const unitCount = units.length;
  // Card del índice: siempre el rollup (lote + UF). El summary del lote va al expandir / ficha.
  const hasUnits = units.length > 0;
  const isSingleVisibleUnit = units.length === 1;
  const isFavorite = lot?.is_favorite === true;
  const isLotContentOnly = lot?.item_type === 'lot_content';
  const hasLotContent = lot?.has_lot_content === true || isLotContentOnly;
  // units vacío ya no implica solo marker: puede ser lot_content.
  const canExpandUnits = hasUnits && !isLotContentOnly;
  const lotContent = hasLotContent ? getLotContentSummary(lot) : null;
  // Se despliega si hay UF o contenido del lote (fila propia, primero).
  const canExpand = canExpandUnits || Boolean(lotContent);
  const contentPadLeft = CARD_PAD_X;

  const showListChips = showListMembership && listFilters.length > 1;
  const lotListLabels = showListChips && (!canExpandUnits || !isExpanded || isSingleVisibleUnit)
    ? (isSingleVisibleUnit && canExpandUnits
      ? getUnitMatchingListLabels(units[0], listFilters, userLists, noListsValue)
      : getLotMatchingListLabels(lot, listFilters, userLists, noListsValue))
    : [];

  const openLot = () => {
    onOpenLot?.(lot.lot_id, { openLotContent: isLotContentOnly });
  };
  const openLotContent = () => {
    onOpenLot?.(lot.lot_id, { openLotContent: true });
  };

  const handleCardClick = () => {
    if (canExpandUnits) {
      if (unitCount > 1) {
        openLot();
        return;
      }
      if (onOpenUnit && units.length === 1) {
        onOpenUnit(lot, units[0]);
        return;
      }
      onToggleExpand?.({ stopPropagation: () => {} }, lot.lot_id);
      return;
    }
    openLot();
  };

  const closeLotMenu = () => setLotMenuAnchor(null);
  const closeUnitMenu = () => setUnitMenu({ anchor: null, unit: null });

  return (
    <Box
      onClick={handleCardClick}
      data-testid="registros-lot-card"
      data-located={locateActive ? 'true' : undefined}
      sx={{
        borderRadius: '8px',
        border: 'none',
        backgroundColor: BG_ELEVATED,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: '#2a2a2a' },
        ...(locateActive ? getRecordCardLocateActiveSx() : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: `${HEADER_GAP}px`,
          px: `${CARD_PAD_X}px`,
          pt: '10px',
          pb: '10px',
        }}
      >
        <RecordLotIdentity
          lot={lot}
          unitCount={canExpandUnits ? unitCount : 0}
          showMarker={isFavorite}
          showLotContent={hasLotContent}
          lotContent={lotContent}
          locationAdornment={canExpand ? <RecordExpandButton expanded={isExpanded} /> : null}
          expanded={isExpanded}
          onLocationClick={canExpand
            ? () => onToggleExpand?.({ stopPropagation: () => {} }, lot.lot_id)
            : null}
          commercialProps={{
            summary: lot.commercial_rollup,
            lotSummary: lot.commercial_summary || null,
            kind: 'rollup',
            showZero: true,
          }}
        >
          {lotListLabels.length > 0 ? (
            <ListMembershipChips labels={lotListLabels} />
          ) : null}
        </RecordLotIdentity>

        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, height: 18, display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Más acciones" placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => setLotMenuAnchor(e.currentTarget)}
              sx={{ ...listCardActionIconSx, ...recordCardHeaderIconSx }}
              aria-label="Más acciones"
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={lotMenuAnchor}
            open={Boolean(lotMenuAnchor)}
            onClose={closeLotMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={ACTION_MENU_PAPER_PROPS}
            MenuListProps={ACTION_MENU_LIST_PROPS}
          >
            <ActionMenuItem
              onClick={() => {
                closeLotMenu();
                onLocateLot?.(lot.lot_id, lot);
              }}
              icon={<MyLocationOutlinedIcon sx={{ fontSize: 20 }} />}
              label={locateActive ? 'Quitar marca del mapa' : 'Ubicar en el mapa'}
            />
            <ActionMenuItem
              onClick={() => {
                closeLotMenu();
                openLot();
              }}
              icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
              label={isLotContentOnly ? 'Ver contenido del lote' : 'Ver lote'}
            />
            {onShareLot && (
              <ActionMenuItem
                onClick={() => {
                  closeLotMenu();
                  // Diferir para que el Menu suelte el foco antes del Dialog (aria-hidden).
                  window.setTimeout(() => {
                    onShareLot(lot);
                  }, 0);
                }}
                icon={<ShareIcon sx={{ fontSize: 20 }} />}
                label="Compartir"
              />
            )}
          </Menu>
        </Box>
      </Box>

      {canExpand && isExpanded && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            bgcolor: BG_UNITS,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          {lotContent ? (
            <Box
              onClick={openLotContent}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                pl: `${contentPadLeft}px`,
                pr: `${CARD_PAD_X}px`,
                py: '10px',
                borderBottom: canExpandUnits && units.length > 0 ? `1px solid ${BORDER}` : 'none',
                cursor: onOpenLot ? 'pointer' : 'default',
                '&:hover': onOpenLot ? { bgcolor: 'rgba(255,255,255,0.03)' } : {},
              }}
            >
              <RecordLotContentIdentity summary={lotContent} />
            </Box>
          ) : null}
          {(canExpandUnits ? units : []).map((unit, idx) => {
            const isLast = idx === units.length - 1;
            const unitListLabels = showListChips && isExpanded && !isSingleVisibleUnit
              ? getUnitMatchingListLabels(unit, listFilters, userLists, noListsValue)
              : [];
            return (
              <Box
                key={unit.id || unit.user_lot_id}
                onClick={() => onOpenUnit?.(lot, unit)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '10px',
                  pl: `${contentPadLeft}px`,
                  pr: `${CARD_PAD_X}px`,
                  py: '10px',
                  borderBottom: isLast ? 'none' : `1px solid ${BORDER}`,
                  cursor: onOpenUnit ? 'pointer' : 'default',
                  '&:hover': onOpenUnit ? { bgcolor: 'rgba(255,255,255,0.03)' } : {},
                }}
              >
                <RecordUnitIdentity
                  unit={unit}
                  showZero
                  location={unitLocationDistinctFromLot(lot, unit)}
                  titleAdornment={unitListLabels.length > 0 ? (
                    <ListMembershipChips labels={unitListLabels} />
                  ) : null}
                />
                <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, height: 18, display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Más acciones" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => setUnitMenu({ anchor: e.currentTarget, unit })}
                      sx={{ ...listCardActionIconSx, ...recordCardHeaderIconSx }}
                      aria-label="Más acciones de la unidad"
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
          <Menu
            anchorEl={unitMenu.anchor}
            open={Boolean(unitMenu.anchor)}
            onClose={closeUnitMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={ACTION_MENU_PAPER_PROPS}
            MenuListProps={ACTION_MENU_LIST_PROPS}
          >
            <ActionMenuItem
              onClick={() => {
                const unit = unitMenu.unit;
                closeUnitMenu();
                if (unit) onOpenUnit?.(lot, unit);
              }}
              icon={<EditIcon sx={{ fontSize: 20 }} />}
              label="Editar"
            />
          </Menu>
        </Box>
      )}
    </Box>
  );
}
